/**
 * <eyewear-prescription-upload>
 *
 * Makes the prescription file actually reach the order.
 *
 * Shopify stores an uploaded line item property itself, so there is no
 * upload endpoint here and nothing to host - the file rides along with the
 * normal add-to-cart POST and comes back as a download link on the order.
 * The catch is that it has to be a REAL form POST. Dawn's <product-form>
 * intercepts submit and posts through fetch() to /cart/add.js, and the AJAX
 * cart silently discards files: the item is added, the property is gone,
 * and nothing anywhere reports a problem.
 *
 * So when - and only when - a file is attached, this steps out of the way
 * of that interception and lets the browser submit natively. Every other
 * add-to-cart on the store keeps the cart notification it has today.
 *
 * The mechanism is a capture-phase listener on the form's PARENT. Capture
 * runs top-down, so it fires before the listener <product-form> put on the
 * form itself; stopPropagation() there means that listener never runs,
 * never calls preventDefault(), and the browser performs the multipart
 * submit. Nothing in Dawn is patched or forked.
 */
if (!customElements.get('eyewear-prescription-upload')) {
  customElements.define(
    'eyewear-prescription-upload',
    class EyewearPrescriptionUpload extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.eyewear-prescription__input');
        this.filePanel = this.querySelector('.eyewear-prescription__file');
        this.fileName = this.querySelector('.eyewear-prescription__file-name');
        this.removeButton = this.querySelector('.eyewear-prescription__remove');
        this.errorPanel = this.querySelector('.eyewear-prescription__error');
        this.maxBytes = parseInt(this.dataset.maxBytes, 10) || 20971520;

        this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
        if (!this.input || !this.form) return;

        this.onChange = this.onChange.bind(this);
        this.onRemove = this.onRemove.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);

        this.input.addEventListener('change', this.onChange);
        this.removeButton?.addEventListener('click', this.onRemove);

        // The parent, not the form: a capture listener on the form itself
        // is not guaranteed to run before another listener already
        // registered on it, but an ancestor's always is.
        this.submitHost = this.form.parentElement || this.form;
        this.submitHost.addEventListener('submit', this.onSubmitCapture, true);

        // The option engine shows and hides this whole block by toggling
        // [hidden]. A hidden-but-enabled input still submits, so a file
        // picked under one lens choice would quietly ride along after the
        // shopper switched to a lens that needs no prescription.
        this.hiddenObserver = new MutationObserver(() => this.syncDisabled());
        this.hiddenObserver.observe(this, { attributes: true, attributeFilter: ['hidden'] });
        this.syncDisabled();
      }

      disconnectedCallback() {
        this.submitHost?.removeEventListener('submit', this.onSubmitCapture, true);
        this.hiddenObserver?.disconnect();
      }

      get file() {
        return this.input?.files?.[0] || null;
      }

      get active() {
        return !this.hidden && !this.input.disabled && !!this.file;
      }

      syncDisabled() {
        this.input.disabled = this.hidden;
        if (this.hidden) this.clear();
      }

      onChange() {
        this.showError('');
        const file = this.file;
        if (!file) return this.renderFile(null);

        if (file.size > this.maxBytes) {
          const limit = Math.round(this.maxBytes / 1048576);
          this.clear();
          this.showError(
            `That file is ${this.formatSize(file.size)}. Shopify accepts up to ${limit} MB — please upload a smaller photo or PDF.`
          );
          return;
        }

        this.renderFile(file);
      }

      onRemove() {
        this.clear();
        this.showError('');
        this.input.focus();
      }

      clear() {
        if (this.input.value) this.input.value = '';
        this.renderFile(null);
      }

      renderFile(file) {
        if (!this.filePanel || !this.fileName) return;
        if (file) {
          this.fileName.textContent = `${file.name} (${this.formatSize(file.size)})`;
          this.filePanel.hidden = false;
        } else {
          this.fileName.textContent = '';
          this.filePanel.hidden = true;
        }
      }

      showError(message) {
        if (!this.errorPanel) return;
        this.errorPanel.textContent = message;
        this.errorPanel.hidden = !message;
      }

      formatSize(bytes) {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
      }

      onSubmitCapture(event) {
        // No file attached: leave Dawn's AJAX add-to-cart exactly as it is,
        // popup and all. Only an actual upload is worth a page reload.
        if (!this.active) return;

        if (this.file.size > this.maxBytes) {
          event.preventDefault();
          event.stopPropagation();
          this.showError('That file is too large to upload. Remove it or choose a smaller one.');
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        // Let the default action through - just keep the event away from
        // <product-form>, whose handler would preventDefault it.
        event.stopPropagation();
      }
    }
  );
}
